import Channel from '../classes/channel';
import Content from '../types/enums/content';
import Features from '../types/enums/features';
import Plan from '../classes/plan';
import Services from '../types/enums/services';
import Storage from '@storage';
import User from '../classes/user';
import { IChannelsState, IUserState } from '../types/interfaces/states';
import { store } from '@shared/store/store';

const ChannelUtils = {
  channelDown(state: IChannelsState) {
    if (!state.filteredChannels?.length) {
      return;
    }
    let index = 0;
    if (state.currentIndex < 0) {
      index = state.currentChannel?.dial
        ? this.getClosestChannel({ state, down: true })
        : 0;
    } else {
      const length = state.filteredChannels.length;
      index = (state.currentIndex - 1 + length) % length;
    }
    state.currentIndex = index;
    state.currentChannel = state.filteredChannels[index];
  },
  channelUp(state: IChannelsState) {
    if (!state.filteredChannels?.length) {
      return;
    }
    let index = 0;
    if (state.currentIndex < 0) {
      index = state.currentChannel?.dial
        ? this.getClosestChannel({ state, up: true })
        : 0;
    } else {
      const length = state.filteredChannels.length;
      index = (state.currentIndex + 1 + length) % length;
    }
    state.currentIndex = index;
    state.currentChannel = state.filteredChannels[index];
  },
  findChannel(id: string) {
    const { channels } = store.getState();
    let channel = channels.channelsData?.find((item) => {
      return item.id === id;
    });
    if (!channel) {
      channel = channels.unavailableChannels?.find((item) => {
        return item.id === id;
      });
    }
    return channel;
  },
  findChannelByName(name: string) {
    const { channels } = store.getState();
    let channel = channels.channelsData?.find((item) => {
      return item.name === name;
    });
    if (!channel) {
      channel = channels.unavailableChannels?.find((item) => {
        return item.name === name;
      });
    }
    return channel;
  },
  findChannelPosition(id: string) {
    const {
      channels: { favouriteChannels },
    } = store.getState();
    return favouriteChannels?.findIndex((ch) => {
      return ch.id === id;
    });
  },
  getAllChannels(filterAvod?: boolean, filterSvod?: boolean) {
    const { channels } = store.getState();
    return [
      ...(channels.channelsData || []),
      ...(channels.hiddenChannels || []),
    ]
      .sort((a: Channel, b: Channel) => {
        return a.dial > b.dial ? 1 : -1;
      })
      .filter((item) => {
        if (filterAvod) {
          return item.type !== Content.AVOD;
        } else if (filterSvod) {
          return item.type !== Content.SVOD;
        } else {
          return true;
        }
      });
  },
  getClosestChannel({
    state,
    down,
    up,
  }: {
    state: IChannelsState;
    down?: boolean;
    up?: boolean;
  }) {
    const channelId = state.currentChannel?.id;
    const channelDial = state.currentChannel?.dial || 0;
    const closestChannel = state?.filteredChannels?.reduce((prev, current) => {
      if (current.id !== channelId && prev.id === channelId) {
        return current;
      }
      return Math.abs(current.dial - channelDial) <=
        Math.abs(prev.dial - channelDial)
        ? current
        : prev;
    });
    if (closestChannel && closestChannel.dial) {
      let index = closestChannel
        ? state.filteredChannels?.indexOf(closestChannel) || 0
        : 0;
      if (down && channelDial < (closestChannel?.dial || 0)) {
        index = Math.max(index - 1, 0);
      }
      if (up && channelDial > (closestChannel?.dial || 0)) {
        index = Math.max(index + 1, 0);
      }
      return index;
    }
    return 0;
  },
  getFavouriteChannels(
    channels: Channel[],
    user: IUserState,
    anonymousId: string
  ) {
    return this.getFilteredChannels(channels, user, true, anonymousId);
  },
  getFilteredChannels(
    channels: Channel[],
    user: IUserState,
    filterFavorite = false,
    anonymousId: string
  ) {
    if (!channels || !channels?.length) return [];
    return channels?.filter((item) => {
      return (
        item &&
        item?.type !== Content.AVOD &&
        item?.type !== Content.SVOD &&
        ((filterFavorite ? item.favorite : true) ||
          item.enableZappingUpgrade ||
          ChannelUtils.isInPlan(item, user, anonymousId) ||
          ChannelUtils.isInExtraPlan(item, user))
      );
    });
  },
  getUnavailableChannels(channels: Channel[]) {
    const {
      channels: { filteredChannels },
    } = store.getState();
    return channels.filter(
      (channel) =>
        (channel.enableZappingUpgrade ||
          !filteredChannels?.includes(channel)) &&
        this.getUpgradablePlan(channel)
    );
  },
  getExtraPlan(channel: Channel) {
    if (!channel?.extraPlans?.length) return null;
    try {
      const { user } = store.getState();
      if (user.extraPlans?.length) {
        const plan = channel.extraPlans.findIndex((item) => {
          const planFound = user.extraPlans?.find((extraPlan) => {
            return extraPlan.id === item.id;
          });
          return planFound;
        });
        if (plan === -1) {
          return channel.extraPlans[0];
        } else {
          return true;
        }
      } else {
        return channel.extraPlans[0];
      }
    } catch (e) {
      return null;
    }
  },
  getNextUpgradablePlan(channel: Channel | undefined, checkRecord: boolean) {
    const { user } = store.getState();
    if (user.isTest) return null;
    if (channel?.upgradablePlans.length) {
      if (checkRecord) {
        const nextAllowedRecordPlan = channel.upgradablePlans.find((plan) => {
          return plan.features.includes(Features.ALLOW_RECORD);
        });
        if (nextAllowedRecordPlan) {
          return nextAllowedRecordPlan;
        }
      }
      return channel.upgradablePlans[0];
    }
    return null;
  },
  getPlanChannels(plan: Plan) {
    const { channels } = store.getState();
    return channels.channelsData?.filter(
      (channel) =>
        channel.plans.includes(plan.id) || channel.extraPlans.includes(plan)
    );
  },
  getUpgradablePlan(
    channel: Channel | undefined,
    checkAllowRecord = false,
    type: Content | null = null
  ) {
    if (!channel?.upgradablePlans?.length && !channel?.extraPlans?.length)
      return null;
    try {
      const { user } = store.getState();
      if (user.isTest) return null;
      const plan = user.isAnonymous ? User.getPlan(null) : user;
      const planId = user.isAnonymous
        ? (plan as Plan)?.id
        : (plan as IUserState).planId;
      const actualPlanIndex = channel.plans?.findIndex((item) => {
        if (checkAllowRecord) {
          return (
            (item === planId &&
              (plan as Plan)?.features?.includes(Features.ALLOW_RECORD)) ||
            (
              user.extraPlans?.find((extraPlan) => extraPlan.id === item)
                ?.features as Features[]
            )?.includes(Features.ALLOW_RECORD)
          );
        } else {
          return item === planId;
        }
      });
      if (
        actualPlanIndex === -1 ||
        (type === Content.MEDIA_PRODUCTION &&
          (user.isAnonymous || User.getPlan(user.planId)?.isFree) &&
          !channel.services.includes(Services.YOU_MISSED_IT_FREE))
      ) {
        const planIndex = channel.upgradablePlans.findIndex((item) => {
          return item.id === user.planId;
        });

        let upgradablePlan: Plan | null = null;
        if (channel.upgradablePlans.length - 1 != planIndex) {
          if (planIndex >= 0) {
            upgradablePlan = channel.upgradablePlans[planIndex + 1];
          } else {
            upgradablePlan = channel.upgradablePlans[0];
          }
        }

        const extraPlan = this.getExtraPlan(channel);
        if (extraPlan === true) {
          return null;
        } else if (upgradablePlan || extraPlan) {
          return upgradablePlan || extraPlan;
        }
      } else {
        return null;
      }
    } catch (e) {}
    return null;
  },
  isInPlan(channel: Channel, user: IUserState, anonymousId: string) {
    return channel.plans.find(
      (item) =>
        item == user.planId || (user.isAnonymous && item === anonymousId)
    );
  },
  isInExtraPlan(channel: Channel, user: IUserState) {
    return channel.extraPlans.find((channelItem) =>
      user.extraPlans?.find(
        (userExtraPlan) => userExtraPlan.id == channelItem.id
      )
    );
  },
  loadChannels(
    state: IChannelsState,
    data: { channels: Channel[]; user: IUserState; anonymousId: string }
  ) {
    const { channels, user, anonymousId } = data;
    state.channelsData = data.channels;
    state.favouriteChannels = this.getFavouriteChannels(
      channels,
      user,
      anonymousId
    );
    state.filteredChannels = this.getFilteredChannels(
      channels,
      user,
      false,
      anonymousId
    );
    this.setChannelByIndex(state, state.currentChannel);
  },
  setChannelByDial(state: IChannelsState, dial?: number) {
    const index =
      state.filteredChannels?.findIndex((value) => value.dial === dial) ??
      state.currentIndex;

    if (index > -1) {
      state.currentIndex = index;
      state.currentChannel = state.filteredChannels?.length
        ? state.filteredChannels[index]
        : undefined;
    }
  },
  setChannelByIndex(state: IChannelsState, channel?: Channel) {
    const index = Math.max(
      state.channelsData?.findIndex((item) => {
        return item.id === channel?.id;
      }) || 0,
      0
    );
    state.currentIndex = channel
      ? state.filteredChannels?.indexOf(channel) || -1
      : -1;
    state.currentChannel = state.channelsData?.length
      ? state.channelsData[index]
      : undefined;
  },
  sortChannels(channels: Channel[]) {
    const sorted = channels.sort((a: Channel, b: Channel) => {
      return a.dial > b.dial ? 1 : -1;
    });
    const storageChannels = Storage.get('channelOrder');
    if (storageChannels) {
      try {
        const parsedChannels = JSON.parse(storageChannels);
        sorted.forEach((ogChannel) => {
          parsedChannels.forEach(
            (orderChannel: { order: number; id: string }) => {
              if (ogChannel.id === orderChannel.id) {
                ogChannel.sortIndex = orderChannel.order;
              }
            }
          );
        });
        const sortedChannels = sorted.sort((channel1, channel2) => {
          return (channel1?.sortIndex ?? 0) > (channel2?.sortIndex ?? 0)
            ? 1
            : -1;
        });
        return sortedChannels;
      } catch (e) {
        console.error(e);
        return sorted;
      }
    } else {
      return sorted;
    }
  },
};

export default ChannelUtils;
